import {
  isNotificationEvent,
  type NotificationEvent,
} from "@steelbill/types";
import { formatRupees } from "@/lib/money";
import type { NotificationRow } from "@/lib/endpoints";

/**
 * Turning a catalogue key and a bag of raw values into a sentence a person can
 * read (spec §11.4, §17.7).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THE SERVER SENDS NO PROSE, AND THAT IS WHY THIS FILE EXISTS.
 *
 * A notification row carries `event` plus a `payload` of raw values — integer
 * paise for money, `26-27/67` for a bill number — and nothing else. Composing
 * the sentence here rather than there buys two things that are not negotiable:
 *
 *   1. THE OWNER READS THIS APP IN MARATHI (§11.4). A server writing the
 *      sentence would have to pick a language for a reader it cannot see, and
 *      the CA and the owner may well want different ones from the same row.
 *   2. EVERY RUPEE GOES THROUGH `formatRupees` EXACTLY ONCE, on the way to a
 *      screen (CLAUDE.md). The API runs on a VPS whose Node may be built with
 *      `small-icu`, where `en-IN` silently degrades to western grouping — a
 *      failure with no error attached to it. Formatting on the client is the
 *      one place the grouping is checked by a test that actually runs.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * THE MAP IS A TOTAL `Record` OVER THE SHARED UNION, so adding an event to
 * `NOTIFICATION_EVENTS` in `@steelbill/types` and forgetting the words for it is
 * a COMPILE ERROR here rather than a row that renders a raw key at the reader.
 * That includes the four events with no emitter yet: they are declared in the
 * catalogue, so they are answered for here.
 */

/** What a line of the hub needs in order to draw itself. */
export interface NotificationCopy {
  /** i18n key for the heading — always present, always a complete sentence. */
  titleKey: string;
  /**
   * i18n key for the detail line, or null when the payload did not carry what
   * the sentence needs. A title with no body is a shorter notification, not a
   * broken one; a body with `{{number}}` left un-substituted is broken.
   */
  bodyKey: string | null;
  /** Interpolation values, ALREADY FORMATTED — money grouped, dates readable. */
  values: Record<string, string>;
  /**
   * Whether this asks the reader to DO something, or merely tells them
   * something happened.
   *
   * ─────────────────────────────────────────────────────────────────────────
   * ONLY ONE EVENT IN THE CATALOGUE IS AN `action`, AND KEEPING IT THAT WAY IS
   * THE POINT. "This bill needs an e-way bill and no number is recorded" is a
   * thing that must be done before goods move — a detention if it is missed.
   * "The number was recorded" is news. Painting both in warning colours would
   * make the colour mean "a notification", which is to say nothing at all, and
   * the reader would stop seeing the one that matters.
   * ─────────────────────────────────────────────────────────────────────────
   */
  tone: "action" | "info";
  /** Which icon the row draws. Resolved to a component by the screen. */
  icon: NotificationIconName;
}

/**
 * Icon NAMES rather than components, so this module stays free of JSX and can be
 * imported by a plain `.ts` test that asserts the wording without rendering.
 */
export type NotificationIconName =
  | "ewayRequired"
  | "ewayRecorded"
  | "delivery"
  | "expiring"
  | "payment"
  | "creditNote"
  | "unknown";

/** What a rule's `body` hands back: the key to render, and what to put in it. */
interface CopyBody {
  key: string;
  values: Record<string, string>;
}

interface CopyRule {
  titleKey: string;
  icon: NotificationIconName;
  tone: NotificationCopy["tone"];
  /**
   * Picks the body and its values out of the payload. Returns null when the
   * payload lacks what the sentence needs — see {@link NotificationCopy.bodyKey}.
   */
  body: (payload: Payload) => CopyBody | null;
}

/** The payload, read defensively — it crossed a wire as `unknown`. */
type Payload = Record<string, unknown>;

function str(payload: Payload, key: string): string | null {
  const value = payload[key];
  return typeof value === "string" && value.trim() !== "" ? value : null;
}

/**
 * An integer-paise field, formatted with Indian grouping.
 *
 * `Number.isInteger` rather than `typeof === "number"`: money is integer paise
 * everywhere in this system (CLAUDE.md), so a float arriving in this field means
 * somebody divided on the way here and the honest response is to render nothing
 * rather than a plausible wrong amount on a compliance alert.
 */
function money(payload: Payload, key: string): string | null {
  const value = payload[key];
  return typeof value === "number" && Number.isInteger(value) ? formatRupees(value) : null;
}

/**
 * The words for every event in the catalogue.
 *
 * THE FOUR EVENTS WITH NO EMITTER ARE ANSWERED FOR ANYWAY. `challan.unbilled`
 * and `quotation.expiring` need a scheduler this repo does not have; `payment
 * .recorded` and `creditnote.issued` need one call each inside a transaction in
 * a route this change did not own. They are in the shared catalogue, so a row
 * could arrive from a newer server than this bundle — and the day one does, it
 * must read as a sentence rather than as `quotation.expiring`. Their payload
 * shapes are therefore read defensively: each falls back to a title-only line if
 * the fields it hoped for are not there.
 */
const COPY: Readonly<Record<NotificationEvent, CopyRule>> = Object.freeze({
  /**
   * THE FLAGSHIP. Rule 138 — the consignment crosses the limit, so an e-way bill
   * must exist before the goods move.
   *
   * THE SENTENCE NAMES BOTH FIGURES, and that is deliberate rather than chatty.
   * "This bill needs an e-way bill" invites "are you sure?" from someone who
   * knows the intra-state limit is ₹1,00,000 and is looking at a ₹96,000 bill —
   * whose consignment value INCLUDING tax is ₹1,13,280. Printing what was
   * compared against what settles that in the notification instead of in a
   * phone call.
   */
  "invoice.eway_bill.required": {
    titleKey: "notif.ewayRequired.title",
    icon: "ewayRequired",
    tone: "action",
    body: (payload): CopyBody | null => {
      const number = str(payload, "number");
      const amount = money(payload, "consignmentPaise");
      const threshold = money(payload, "thresholdPaise");
      if (number === null || amount === null || threshold === null) return null;
      /*
       * The buyer's name is worth having and is not worth failing over. It comes
       * from the document's FROZEN snapshot (§4), which can be unreadable on an
       * old row — so the sentence has two forms rather than printing "Bill
       * 26-27/67 for null".
       */
      const customer = str(payload, "customerName");
      return customer === null
        ? { key: "notif.ewayRequired.body", values: { number, amount, threshold } }
        : {
            key: "notif.ewayRequired.bodyNamed",
            values: { number, amount, threshold, customer },
          };
    },
  },

  "invoice.eway_bill.recorded": {
    titleKey: "notif.ewayRecorded.title",
    icon: "ewayRecorded",
    tone: "info",
    body: (payload) => {
      const number = str(payload, "number");
      const ewbNo = str(payload, "ewbNo");
      return number === null || ewbNo === null
        ? null
        : { key: "notif.ewayRecorded.body", values: { number, ewbNo } };
    },
  },

  "challan.eway_bill.recorded": {
    titleKey: "notif.ewayRecorded.title",
    icon: "ewayRecorded",
    tone: "info",
    body: (payload) => {
      const number = str(payload, "number");
      const ewbNo = str(payload, "ewbNo");
      return number === null || ewbNo === null
        ? null
        : { key: "notif.ewayRecordedChallan.body", values: { number, ewbNo } };
    },
  },

  "challan.unbilled": {
    titleKey: "notif.challanUnbilled.title",
    icon: "delivery",
    tone: "info",
    body: (payload) => {
      const number = str(payload, "number");
      return number === null
        ? null
        : { key: "notif.challanUnbilled.body", values: { number } };
    },
  },

  "quotation.expiring": {
    titleKey: "notif.quotationExpiring.title",
    icon: "expiring",
    tone: "info",
    body: (payload) => {
      const number = str(payload, "number");
      return number === null
        ? null
        : { key: "notif.quotationExpiring.body", values: { number } };
    },
  },

  "payment.recorded": {
    titleKey: "notif.paymentRecorded.title",
    icon: "payment",
    tone: "info",
    body: (payload) => {
      const number = str(payload, "number");
      const amount = money(payload, "amountPaise");
      return number === null || amount === null
        ? null
        : { key: "notif.paymentRecorded.body", values: { number, amount } };
    },
  },

  "creditnote.issued": {
    titleKey: "notif.creditNoteIssued.title",
    icon: "creditNote",
    tone: "info",
    body: (payload) => {
      const number = str(payload, "number");
      return number === null
        ? null
        : { key: "notif.creditNoteIssued.body", values: { number } };
    },
  },
});

/**
 * The sentence for one row.
 *
 * AN UNKNOWN EVENT STILL RENDERS. A server deployed ahead of this bundle can
 * write a row whose key is not in the union compiled into it — the flag exists
 * precisely so the hub can ship before every emitter does. Such a row gets a
 * neutral heading and keeps its deep link, because a notification the reader
 * cannot understand but CAN open is still better than a blank line, and far
 * better than a crash that takes the whole inbox down with it.
 */
export function describeNotification(row: NotificationRow): NotificationCopy {
  if (!isNotificationEvent(row.event)) {
    return {
      titleKey: "notif.unknown.title",
      bodyKey: null,
      values: {},
      tone: "info",
      icon: "unknown",
    };
  }

  const rule = COPY[row.event];
  const payload =
    typeof row.payload === "object" && row.payload !== null
      ? (row.payload as Payload)
      : {};
  const body = rule.body(payload);

  return {
    titleKey: rule.titleKey,
    bodyKey: body?.key ?? null,
    values: body?.values ?? {},
    tone: rule.tone,
    icon: rule.icon,
  };
}
